import type { ItemId } from "./items.js";
import type { SkillId } from "./skills.js";
import type { ZoneId } from "./zones.js";

export enum Tile {
  Grass = 0,
  GrassAlt = 1,
  Path = 2,
  Sand = 3,
  Water = 4,
  Tree = 5,
  Fence = 6,
  Floor = 7,
  Flowers = 8,
  Rock = 9,
  Bush = 10,
  /** Raised wooden pier / dock planks over water. */
  Pier = 11,
  /** Outdoor basketball asphalt (dark grey). */
  CourtBasket = 12,
  /** Hard tennis court asphalt (green). */
  CourtTennis = 13,
  /** Painted court lines (white) over sports asphalt. */
  CourtLine = 14,
}

export const WALKABLE_TILES: ReadonlySet<Tile> = new Set([
  Tile.Grass,
  Tile.GrassAlt,
  Tile.Path,
  Tile.Sand,
  Tile.Floor,
  Tile.Flowers,
  Tile.Pier,
  Tile.CourtBasket,
  Tile.CourtTennis,
  Tile.CourtLine,
]);

export function isWalkableTile(tile: Tile | undefined): boolean {
  return tile !== undefined && WALKABLE_TILES.has(tile);
}

export type NodeKind = "digSpot" | "fetchSpot" | "forageSpot" | "begSpot" | "forge" | "bank" | "npc";

export type TravelTag =
  | "Hub"
  | "DigPark"
  | "FetchWoods"
  | "BasketCourt"
  | "Meadow"
  | "ForgeDen"
  | "Vault"
  | "MyKennel"
  | "__Neighborhood__"
  | "__TravelMenu__"
  | `VisitKennel:${string}`;

/** How a begging human is posed in the world (not always grilling). */
export type BegPose = "sit" | "cook" | "eat" | "chat";

export interface ResourceNodeDef {
  id: string;
  kind: NodeKind;
  name: string;
  skillId?: SkillId;
  x: number;
  y: number;
  /** Gather item this node yields (dig/fetch). */
  itemId?: ItemId;
  reqLevel?: number;
  xp?: number;
  durationMs?: number;
  respawnMs?: number;
  npcKind?: NpcKind;
  /** Spoken when you click a flavor NPC. */
  line?: string;
  /** Client pose for beg spots (cookouts mix cook / eat / chat / sit). */
  begPose?: BegPose;
}

export type NpcKind = "banker" | "barksmith" | "shopkeeper" | "guide" | "vendor" | "performer";

export type CritterKind = "squirrel" | "rabbit" | "pigeon" | "duck" | "duckling";

export type SceneryKind =
  | "hotdogStand"
  | "iceCreamCart"
  | "pretzelCart"
  | "balloonCart"
  | "popcornCart"
  | "sign"
  | "basketHoop"
  | "tennisNet"
  | "stage"
  | "bench"
  | "lamp"
  | "stadiumSeat"
  | "bridgeRail"
  | "drumCircle"
  | "pavilion"
  | "bbqGrill"
  | "picnicTable"
  | "frisbeeGame"
  | "picnic"
  | "lawnSprayer"
  | "swingSet"
  | "slide"
  | "climber"
  | "fishingSpot";

export interface AmbientWalkerDef {
  id: string;
  name: string;
  fur: number;
  /** Interest / patrol anchors the pup visits. */
  waypoints: { x: number; y: number }[];
  speed?: number;
  /**
   * When set, this walker renders like a real player (`Name · Total N`) and runs a
   * behavioral loop (walk, skill, idle, bark) instead of a pure scenic patrol.
   */
  totalLevel?: number;
  /** Optional collar tint (item color seed); omit for none. */
  collar?: number | null;
  /** Soft personality bias for the AI loop. */
  role?: "wander" | "gather" | "social" | "digger" | "forager" | "beggar";
}

/** Human + leashed dog strolling open lawn (client ambient). */
export interface AmbientLeashWalkerDef {
  id: string;
  /** Owner display name. */
  name: string;
  /** Pup display name. */
  dogName: string;
  homeX: number;
  homeY: number;
  /** Roam radius around home (tiles). */
  roam?: number;
  /** Optional human look seed. */
  humanVariant?: number;
  /** Optional dog fur / breed seed. */
  dogFur?: number;
}

/** Small wildlife - client-simulated; flees dogs into trees / bushes / edges. */
export interface AmbientCritterDef {
  id: string;
  kind: CritterKind;
  homeX: number;
  homeY: number;
  /** Roam radius around home while calm. */
  roam?: number;
  /**
   * Duck family: when set on a `duck` entry, spawns a mother + this many ducklings
   * that parade in a line, scatter from dogs, then reform.
   */
  familySize?: number;
}

/** Park scenery (vendor carts, signs) placed in world space. */
export interface SceneryDef {
  id: string;
  kind: SceneryKind;
  x: number;
  y: number;
  /** Optional world label (e.g. zone sign). */
  label?: string;
  /** Optional yaw in radians (benches face the stage, etc.). */
  rotY?: number;
  /** Optional vertical lift in world units (amphitheater tiers / sunken stage). */
  yLift?: number;
  /** Optional local-X scale (shorter pier rail ends / corners). */
  scaleX?: number;
  /** Optional local-X offset after rotY (keeps short rails flush on the deck). */
  localOx?: number;
}

export interface TeleporterDef {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  tag: TravelTag;
  label: string;
}

/** Painted ground markers (e.g. first-timer spawn). */
export interface GroundMarkerDef {
  id: string;
  kind: "spawn";
  x: number;
  y: number;
  w: number;
  h: number;
  /** Optional floating label; omit for icon-only marks. */
  label?: string;
}

export interface MapData {
  width: number;
  height: number;
  zoneId: ZoneId;
  name: string;
  tiles: Tile[][];
  nodes: ResourceNodeDef[];
  teleporters: TeleporterDef[];
  walkers?: AmbientWalkerDef[];
  /** Humans walking dogs on open lawns (client ambient). */
  leashWalkers?: AmbientLeashWalkerDef[];
  markers?: GroundMarkerDef[];
  scenery?: SceneryDef[];
  critters?: AmbientCritterDef[];
  /** Smooth sidewalk centerlines for curved ribbon meshes (tiles stay for walkability). */
  pathRibbons?: PathRibbonDef[];
  /** Painted sports-court lines in tile space (thin strips, not full tiles). */
  courtMarks?: CourtMarkDef[];
  /**
   * Extra blocked cells from scenery / furniture / decorative props.
   * Same dimensions as `tiles`. Built by `bakeMapCollision`.
   */
  blocked?: boolean[][];
}

/** Curved sidewalk ribbon (visual centerline; Path tiles still mark walkability). */
export interface PathRibbonDef {
  points: { x: number; y: number }[];
  /** Full width in tiles. */
  width: number;
}

/** Axis-aligned or diagonal court line segment in tile coordinates. */
export interface CourtMarkDef {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Soft collision radius (in tiles) around solid tile centers for continuous poses. */
const SOLID_FOOTPRINT: Partial<Record<Tile, number>> = {
  [Tile.Tree]: 0.9,
  [Tile.Bush]: 0.72,
  [Tile.Rock]: 0.78,
  [Tile.Fence]: 0.55,
  [Tile.Water]: 0.62,
};

/** Soft radius around baked object blockers (benches, lamps, carts, …). */
const BLOCKED_OBJECT_RADIUS = 0.52;

function touchesPath(map: MapData, x: number, y: number): boolean {
  return (
    map.tiles[y]?.[x - 1] === Tile.Path ||
    map.tiles[y]?.[x + 1] === Tile.Path ||
    map.tiles[y - 1]?.[x] === Tile.Path ||
    map.tiles[y + 1]?.[x] === Tile.Path
  );
}

/**
 * Continuous pose ok for ambient park life (roaming dogs / leash walkers).
 * Same as player walkability, plus keep clear of travel/spawn pads.
 */
export function isAmbientWalkPos(map: MapData, x: number, y: number): boolean {
  if (!isWalkable(map, Math.round(x), Math.round(y))) {
    return false;
  }
  if (!clearsSolidFootprint(map, x, y)) {
    return false;
  }
  // Integer pad check on the feet tile (and a light margin via isTravelPadCell).
  if (isTravelPadCell(map, Math.round(x), Math.round(y), 1)) {
    return false;
  }
  return true;
}

/**
 * True if a tile sits on/near a travel portal or spawn pad.
 * Keep benches, lamps, and other décor out of these footprints.
 */
export function isTravelPadCell(map: MapData, x: number, y: number, margin = 2): boolean {
  for (const pad of map.teleporters ?? []) {
    if (
      x >= pad.x - margin &&
      x < pad.x + pad.w + margin &&
      y >= pad.y - margin &&
      y < pad.y + pad.h + margin
    ) {
      return true;
    }
  }
  for (const mark of map.markers ?? []) {
    if (mark.kind !== "spawn") {
      continue;
    }
    if (
      x >= mark.x - margin &&
      x < mark.x + mark.w + margin &&
      y >= mark.y - margin &&
      y < mark.y + mark.h + margin
    ) {
      return true;
    }
  }
  return false;
}

/** True if a sports court tile is within `radius` cells (keeps décor off the apron). */
function nearSportsCourt(map: MapData, x: number, y: number, radius: number): boolean {
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const t = map.tiles[y + dy]?.[x + dx];
      if (t === Tile.CourtBasket || t === Tile.CourtTennis || t === Tile.CourtLine) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Lamp posts only on grass beside sidewalk `Path` tiles (not random lawn).
 * Matches client `ParkView.drawProps`.
 */
export function isSidewalkLampCell(map: MapData, x: number, y: number): boolean {
  const tile = map.tiles[y]?.[x];
  if (tile !== Tile.Grass && tile !== Tile.GrassAlt) {
    return false;
  }
  if (isTravelPadCell(map, x, y)) {
    return false;
  }
  // Keep court aprons clear - no lamps hugging tennis / basketball asphalt.
  if (nearSportsCourt(map, x, y, 4)) {
    return false;
  }
  if (!touchesPath(map, x, y)) {
    return false;
  }
  // Prefer the outer/north-west curb so both sides of a wide walk don't double up.
  const pathSouth = map.tiles[y + 1]?.[x] === Tile.Path;
  const pathEast = map.tiles[y]?.[x + 1] === Tile.Path;
  if (pathSouth || pathEast) {
    return false;
  }
  // Sparse - roughly one lamp every ~18-20 pathside curb cells.
  return (x * 17 + y * 29) % 19 === 0;
}

/** Occasional benches on the same sidewalk edge, offset from lamps. */
export function isPathsideBenchCell(map: MapData, x: number, y: number): boolean {
  const tile = map.tiles[y]?.[x];
  if (tile !== Tile.Grass && tile !== Tile.GrassAlt) {
    return false;
  }
  if (isTravelPadCell(map, x, y)) {
    return false;
  }
  // Keep court aprons clear - no benches on / hugging basketball or tennis.
  if (nearSportsCourt(map, x, y, 4)) {
    return false;
  }
  if (!touchesPath(map, x, y)) {
    return false;
  }
  const pathSouth = map.tiles[y + 1]?.[x] === Tile.Path;
  const pathEast = map.tiles[y]?.[x + 1] === Tile.Path;
  if (pathSouth || pathEast) {
    return false;
  }
  // Rarer than lamps - a few rest spots per district, not a continuous row.
  return (x * 17 + y * 29) % 23 === 11;
}

/** @deprecated Prefer isSidewalkLampCell / isPathsideBenchCell (map-aware). */
export function isDecorativePropCell(tile: Tile, x: number, y: number): boolean {
  return (tile === Tile.Grass || tile === Tile.GrassAlt) && (x * 17 + y * 29) % 47 === 0;
}

/** Nodes with solid world props - stand beside them, not on top. */
export function isSolidNode(node: ResourceNodeDef): boolean {
  if (
    node.kind === "forge" ||
    node.kind === "bank" ||
    node.kind === "begSpot" ||
    node.kind === "digSpot" ||
    node.kind === "fetchSpot" ||
    node.kind === "forageSpot"
  ) {
    return true;
  }
  if (node.kind !== "npc") {
    return false;
  }
  // Stationary service NPCs block; flavor guides wander and stay walkable.
  return npcProvidesService(node.npcKind);
}

/** Bank / forge UIs - not used as Begging targets. */
export function npcIsSpecialService(kind: NpcKind | undefined): boolean {
  return kind === "banker" || kind === "barksmith";
}

/** Bank, forge, or snack vendor - stay put and show a shop icon. */
export function npcProvidesService(kind: NpcKind | undefined): boolean {
  return kind === "banker" || kind === "barksmith" || kind === "vendor";
}

/** Flavor NPCs with no shop UI - wander the park (except stage performers). */
export function npcIsWanderer(kind: NpcKind | undefined): boolean {
  if (kind === "performer") {
    return false;
  }
  return !npcProvidesService(kind);
}

/** Any NPC that isn't bank/forge can be petted or begged for Begging. */
export function npcAllowsSkill(kind: NpcKind | undefined): boolean {
  return !npcIsSpecialService(kind);
}

function markBlocked(blocked: boolean[][], width: number, height: number, x: number, y: number): void {
  if (x < 0 || y < 0 || x >= width || y >= height) {
    return;
  }
  const row = blocked[y];
  if (row) {
    row[x] = true;
  }
}

/**
 * Stamp scenery, decorative benches/lamps, and solid nodes into `map.blocked`.
 * That grid is the park “navmesh”: A* pathfinding walks only free cells so
 * pups route around furniture automatically on both client and server.
 */
export function bakeMapCollision(map: MapData): MapData {
  const blocked: boolean[][] = Array.from({ length: map.height }, () =>
    Array.from({ length: map.width }, () => false),
  );

  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (isSidewalkLampCell(map, x, y) || isPathsideBenchCell(map, x, y)) {
        markBlocked(blocked, map.width, map.height, x, y);
      }
    }
  }

  for (const item of map.scenery ?? []) {
    if (
      item.kind === "bridgeRail" ||
      item.kind === "drumCircle" ||
      item.kind === "pavilion" ||
      item.kind === "frisbeeGame" ||
      item.kind === "picnic" ||
      item.kind === "lawnSprayer" ||
      item.kind === "swingSet" ||
      item.kind === "slide" ||
      item.kind === "climber" ||
      item.kind === "fishingSpot"
    ) {
      // Open shelters / gathering props - walkable under or around.
      // Lawn sprayers are slim stakes; pups walk past to trigger the spray.
      // Playground gear is meant to be run through / under.
      // Pier anglers stand on the deck; pups can still pass.
      continue;
    }
    const cx = Math.round(item.x);
    const cy = Math.round(item.y);
    if (item.kind === "stage") {
      // Stage deck (~11×6 tiles after prop scale) - dogs stay in the audience.
      for (let oy = -4; oy <= 4; oy++) {
        for (let ox = -6; ox <= 6; ox++) {
          markBlocked(blocked, map.width, map.height, cx + ox, cy + oy);
        }
      }
      continue;
    }
    markBlocked(blocked, map.width, map.height, cx, cy);
    // Compact grill / table - don't claim a full 5-cell footprint.
    if (item.kind === "bbqGrill" || item.kind === "picnicTable") {
      continue;
    }
    // Carts / stands spill onto neighbors after prop scale.
    if (item.kind !== "sign" && item.kind !== "lamp") {
      markBlocked(blocked, map.width, map.height, cx - 1, cy);
      markBlocked(blocked, map.width, map.height, cx + 1, cy);
      markBlocked(blocked, map.width, map.height, cx, cy - 1);
      markBlocked(blocked, map.width, map.height, cx, cy + 1);
    }
  }

  for (const node of map.nodes) {
    if (isSolidNode(node)) {
      markBlocked(blocked, map.width, map.height, node.x, node.y);
    }
  }

  map.blocked = blocked;
  return map;
}

export function tileAt(map: MapData, x: number, y: number): Tile | undefined {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) {
    return undefined;
  }
  return map.tiles[y]?.[x];
}

export function isBlockedByObject(map: MapData, x: number, y: number): boolean {
  return map.blocked?.[y]?.[x] === true;
}

export function isWalkable(map: MapData, x: number, y: number): boolean {
  if (!isWalkableTile(tileAt(map, x, y))) {
    return false;
  }
  return !isBlockedByObject(map, x, y);
}

/** Continuous pose clearance against oversized solid props (trees, rocks, benches, …). */
export function clearsSolidFootprint(map: MapData, x: number, y: number): boolean {
  const cx = Math.round(x);
  const cy = Math.round(y);
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const tx = cx + dx;
      const ty = cy + dy;
      const tile = tileAt(map, tx, ty);
      if (tile === undefined) {
        continue;
      }
      const radius = SOLID_FOOTPRINT[tile];
      if (radius !== undefined && Math.hypot(x - tx, y - ty) < radius) {
        return false;
      }
      // Benches, lamps, carts, forge, etc. baked into `blocked`.
      if (isBlockedByObject(map, tx, ty) && Math.hypot(x - tx, y - ty) < BLOCKED_OBJECT_RADIUS) {
        return false;
      }
    }
  }
  return true;
}

export interface NodeRuntime {
  id: string;
  /** Epoch ms when the spot becomes harvestable again (0 = available). */
  depletedUntil: number;
  /** Remaining harvests before rest; 0 while depleted. */
  charges: number;
}
